import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Modal, Spin, Alert } from 'antd';
import { documentKind, safeDocumentUrl } from '../../utils/certificate';
import { getProxyUrl } from '../../utils/urlProxy';
import './DocumentPreviewModal.css';

const PdfDocument = lazy(() => import('./PdfDocument'));

// Word content is isolated from the label in a script-free, network-free frame.
export async function renderWordDocument(buffer) {
  const [{ renderAsync }, { default: DOMPurify }] = await Promise.all([import('docx-preview'), import('dompurify')]);
  const body = document.createElement('div');
  await renderAsync(buffer, body, undefined, { useBase64URL: true, renderAltChunks: false, ignoreLastRenderedPageBreak: false });
  const html = DOMPurify.sanitize(body.innerHTML, { ADD_TAGS: ['style'], FORBID_TAGS: ['iframe', 'object', 'embed', 'form', 'input', 'button'], FORBID_ATTR: ['srcset'] });
  return `<!doctype html><html lang="zh-CN"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; font-src data: blob:; style-src 'unsafe-inline'; form-action 'none'"><style>body{margin:0;background:#edf1f5}.docx-wrapper{padding:20px!important;background:transparent!important}.docx-wrapper>section.docx{background:#fff;box-shadow:0 2px 16px #182c4512!important;max-width:100%;box-sizing:border-box}@media(max-width:600px){.docx-wrapper{padding:8px!important}.docx-wrapper>section.docx{padding:24px 18px!important;min-height:0!important}table{max-width:100%}img{max-width:100%;height:auto}}</style></head><body>${html}</body></html>`;
}

export default function DocumentPreviewModal({ attachment, onClose }) {
  const file = attachment?.file || {};
  const kind = documentKind(file);
  const url = getProxyUrl(safeDocumentUrl(file.url));
  const [word, setWord] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (kind !== 'docx' || !url) return;
    const controller = new AbortController();
    let active = true;
    setWord(''); setError('');
    (async () => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await renderWordDocument(await response.arrayBuffer());
        if (active) setWord(html);
      } catch (err) { if (active) setError('证书加载失败，请重试或下载原文件。'); }
    })();
    return () => { active = false; controller.abort(); };
  }, [kind, url]);

  return <Modal open title={attachment?.name || '检测证书'} onCancel={onClose} footer={null} width="min(1100px, 96vw)" style={{ top: 16 }} className="certificate-modal" destroyOnClose>
    <div className="certificate-toolbar"><span>{file.name || '报告文件'}</span>{url && <a href={url} download={file.name || '检测证书'} target="_blank" rel="noopener noreferrer">下载原文件</a>}</div>
    <div className="certificate-document">
      {!url ? <Alert type="info" message="尚未上传证书，请在“检验报告”中上传 PDF 或 Word（.docx）文件。" />
        : kind === 'pdf' ? <Suspense fallback={<Spin tip="正在打开证书"><div className="certificate-loading" /></Spin>}><PdfDocument url={url} /></Suspense>
        : kind === 'docx' ? error ? <Alert type="error" showIcon message={error} /> : word ? <iframe title="Word 检测证书预览" sandbox="" srcDoc={word} /> : <Spin tip="正在打开 Word 证书"><div className="certificate-loading" /></Spin>
        : <Alert type="info" message={kind === 'doc' ? '旧版 .doc 暂不支持在线预览，请将文件另存为 .docx 或 PDF 后上传。原文件仍可下载。' : '此文件格式暂不支持在线预览，请下载原文件查看。'} />}
    </div>
  </Modal>;
}
